import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { isValidMonthDay, updateMeSchema, userDtoSchema } from '@tg-hookah/shared/dto';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { formatPhoneMask, normalisePhone } from '@/features/booking/phone';
import { BookingHistory } from './BookingHistory';
import { toast } from '@/components/Toast';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function ProfileView() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [phone, setPhone] = useState<string>(user?.phone ? formatPhoneMask(user.phone) : '+7');
  const [birthMonth, setBirthMonth] = useState<string>(user?.birthMonth ? String(user.birthMonth) : '');
  const [birthDay, setBirthDay] = useState<string>(user?.birthDay ? String(user.birthDay) : '');

  const monthNum = birthMonth ? Number(birthMonth) : null;
  const maxDay = monthNum && monthNum >= 1 && monthNum <= 12 ? DAYS_IN_MONTH[monthNum - 1]! : 31;

  const save = useMutation({
    mutationFn: async (input: { phone: string; birthMonth: string; birthDay: string }) => {
      const monthNumber = input.birthMonth ? Number(input.birthMonth) : null;
      const dayNumber = input.birthDay ? Number(input.birthDay) : null;
      // Both empty → clear DOB. Otherwise both required and must form a valid date.
      if (monthNumber === null && dayNumber === null) {
        // ok — clearing
      } else if (
        monthNumber === null ||
        dayNumber === null ||
        !isValidMonthDay(monthNumber, dayNumber)
      ) {
        throw new Error('Укажите корректную дату — день и месяц');
      }
      // An empty field (or a bare "+7") clears the phone — send '' so the server
      // nulls it, instead of failing phoneSchema's min-length regex.
      const normalised = normalisePhone(input.phone);
      const phoneForApi = normalised.replace(/\D/g, '').length <= 1 ? '' : normalised;
      const payload = updateMeSchema.parse({
        phone: phoneForApi,
        birthMonth: monthNumber,
        birthDay: dayNumber,
      });
      const res = await api.patch('/me', payload);
      return userDtoSchema.parse(res.data);
    },
    onSuccess: (u) => {
      setUser(u);
      toast('Сохранено', 'success');
    },
    onError: (err) => toast((err as Error).message ?? t('common.error'), 'error'),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-16 rounded-full overflow-hidden inline-flex items-center justify-center"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid var(--line-2)',
          }}
        >
          {user?.photoUrl ? (
            <img src={user.photoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-xl font-extrabold">
              {(user?.firstName ?? '?').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold tracking-tight truncate">
            {user?.firstName} {user?.lastName ?? ''}
          </div>
          {user?.username ? (
            <div className="font-mono text-[11px] tracking-[0.1em] text-white/45 truncate">
              @{user.username}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="eyebrow">Телефон</span>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhoneMask(e.target.value))}
            className="rounded-xl bg-white/[0.03] px-3.5 py-3 outline-none border border-white/10 focus:border-white/35 focus:bg-white/[0.05] transition-colors"
          />
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="eyebrow">Дата рождения</span>
          <div className="grid grid-cols-2 gap-2">
            <select
              aria-label="День"
              value={birthDay}
              onChange={(e) => setBirthDay(e.target.value)}
              className="rounded-xl bg-white/[0.03] px-3.5 py-3 outline-none border border-white/10 focus:border-white/35 focus:bg-white/[0.05] transition-colors"
            >
              <option value="">День</option>
              {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select
              aria-label="Месяц"
              value={birthMonth}
              onChange={(e) => {
                const newMonth = e.target.value;
                setBirthMonth(newMonth);
                // Clamp day if previous selection is now invalid for the new month.
                if (newMonth && birthDay) {
                  const newMax = DAYS_IN_MONTH[Number(newMonth) - 1]!;
                  if (Number(birthDay) > newMax) setBirthDay(String(newMax));
                }
              }}
              className="rounded-xl bg-white/[0.03] px-3.5 py-3 outline-none border border-white/10 focus:border-white/35 focus:bg-white/[0.05] transition-colors"
            >
              <option value="">Месяц</option>
              {MONTH_NAMES.map((name, i) => (
                <option key={i} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate({ phone, birthMonth, birthDay })}
          className="mt-1 rounded-full py-3 font-display uppercase tracking-[0.08em] text-[13px] disabled:opacity-60"
          style={{ background: '#ffffff', color: '#0a0a0b' }}
        >
          {t('profile.savePhone')}
        </button>
      </div>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <span className="eyebrow">{t('profile.history')}</span>
          <span className="flex-1 hairline" />
        </div>
        <BookingHistory />
      </section>
    </div>
  );
}
